// HT-2 · 承認する（受信箱：分類ごとのカード + 似た候補のグループ + 詳細）
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Prohibit, Users } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { LEARN, authHeader, detailOf, Panel, Pill, Empty, Help } from './shared';
import ReadingConfig from './ReadingConfig';
import ProposalDetail from './ProposalDetail';

function ProposalRow({ row, onOpen }) {
  return (
    <div className="rounded-md border border-zinc-200 px-3 py-2.5"
      data-testid={`approve-card-${row.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[12.5px] font-medium text-zinc-900">{row.title_ja}</p>
          <p className="flex flex-wrap gap-1.5 pt-1.5">
            <Pill testId={`approve-category-${row.id}`}>{row.category_label_ja}</Pill>
            <Pill tone={row.risk === 'high' ? 'rose' : row.risk === 'medium' ? 'amber' : 'teal'}
              testId={`approve-risk-${row.id}`}>影響の大きさ: {row.risk_size_ja}</Pill>
            <Pill tone={row.conflict_count ? 'rose' : 'zinc'}
              testId={`approve-conflict-${row.id}`}>{row.conflict_state_ja}</Pill>
            <Pill tone={row.status === 'approved' ? 'teal'
              : row.status === 'rejected' ? 'rose'
                : row.status === 'needs_examples' ? 'amber' : 'zinc'}
              testId={`approve-status-${row.id}`}>{row.status_label_ja}</Pill>
            {row.group_id && (
              <Pill testId={`approve-group-pill-${row.id}`}>似た候補としてまとめています</Pill>
            )}
          </p>
          <p className="pt-1.5 text-[11.5px] text-zinc-700">
            元の書類 {row.source_document_count}件 · 支えとなる修正 {row.supporting_correction_count}件 ·
            正しい例 {row.positive_example_count}件 · 誤りやすい例 {row.negative_example_count}件
          </p>
          <p className="text-[11.5px] text-zinc-700">
            対象: {row.profiles_affected_labels_ja?.join(' / ') || '指定なし'} ·
            推奨する範囲: {row.recommended_scope_ja}（{row.scope_summary_ja}）
          </p>
          <p className="text-[11px] text-zinc-500">
            提出者 {row.submitted_by || '—'} · 最終更新 {row.updated_at || '—'}
          </p>
          <p className="pt-1 text-[11.5px] text-teal-800"
            data-testid={`approve-next-${row.id}`}>次にすること: {row.next_action_ja}</p>
        </div>
        <Button size="sm" className="h-9 shrink-0 bg-teal-700 text-[12px] hover:bg-teal-800"
          onClick={() => onOpen(row.id)} data-testid={`approve-open-${row.id}`}>
          内容を確認
        </Button>
      </div>
    </div>
  );
}

export default function Approve({ onChanged }) {
  const [view, setView] = useState('proposed');
  const [category, setCategory] = useState('');
  const [risk, setRisk] = useState('');
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${LEARN}/proposals/inbox`, {
        params: { view, ...(category ? { category } : {}), ...(risk ? { risk } : {}) },
        headers: authHeader(),
      });
      setData(r.data);
    } catch (e) { toast.error(detailOf(e, '学習候補を取得できませんでした')); }
  }, [view, category, risk]);
  useEffect(() => { load(); }, [load]);

  const groups = data?.groups || [];
  const rows = data?.rows || [];
  const grouped = groups.filter((g) => rows.some((r) => r.group_id === g.id));

  const splitAll = async (gid) => {
    try {
      await axios.post(`${LEARN}/proposal-groups/${gid}/split`, {},
        { headers: authHeader() });
      toast.success('グループを解除しました');
      load();
    } catch (e) { toast.error(detailOf(e, '解除できませんでした')); }
  };

  return (
    <div className="space-y-4">
      <ReadingConfig />

      <Panel title="承認する" testId="learn-approve"
        hint="承認しただけでは、今後の読み取りには使用されません。HT-3で検証し、有効にした内容だけが使用されます。"
        action={(
          <div className="flex flex-wrap gap-1.5">
            {(data?.views || []).map((v) => (
              <button key={v.key} type="button" onClick={() => setView(v.key)}
                aria-pressed={view === v.key}
                className={`min-h-[32px] rounded-full border px-3 text-[12px] ${
                  view === v.key ? 'border-teal-700 bg-teal-700 text-white'
                    : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50'}`}
                data-testid={`approve-view-${v.key}`}>
                {v.label_ja}<span className="pl-1 tabular-nums">{v.count}</span>
              </button>
            ))}
          </div>
        )}>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[11.5px] text-zinc-600" htmlFor="approve-category">分類</label>
          <select id="approve-category" value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-9 rounded-md border border-zinc-200 px-2 text-[12px]"
            data-testid="approve-category">
            <option value="">すべて</option>
            {(data?.categories || []).map((c) => (
              <option key={c.key} value={c.key}>{c.label_ja}</option>
            ))}
          </select>
          <label className="text-[11.5px] text-zinc-600" htmlFor="approve-risk">影響の大きさ</label>
          <select id="approve-risk" value={risk} onChange={(e) => setRisk(e.target.value)}
            className="h-9 rounded-md border border-zinc-200 px-2 text-[12px]"
            data-testid="approve-risk-filter">
            <option value="">すべて</option>
            {(data?.risk_options || []).map((o) => (
              <option key={o.key} value={o.key}>{o.size_ja}</option>
            ))}
          </select>
          <Help label="学習候補" testId="approve-help-proposal">
            人が直した内容から作られた、今後の読み取りで再利用できる案です。
            承認しても、検証して有効にするまでは使われません。
          </Help>
        </div>

        {rows.length === 0 && (
          <div className="pt-3">
            <Empty testId="approve-empty">
              {view === 'proposed' ? '確認が必要な学習候補はありません。'
                : view === 'needs_examples' ? '追加例が必要な学習候補はありません。'
                  : view === 'conflicted' ? '不一致のある学習候補はありません。'
                    : view === 'approved' ? '承認済みの学習候補はありません。'
                      : view === 'rejected' ? '却下した学習候補はありません。'
                        : view === 'deferred' ? '「後で確認」にした学習候補はありません。'
                          : '学習候補はまだありません。'}
            </Empty>
          </div>
        )}

        {grouped.length > 0 && (
          <div className="space-y-2 pt-3" data-testid="approve-groups">
            {grouped.map((g) => (
              <div key={g.id} className="rounded-md border border-teal-200 bg-teal-50/40 p-2.5"
                data-testid={`approve-group-${g.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="flex items-center gap-1.5 text-[12px] font-medium text-zinc-900">
                    <Users size={13} />似た候補 {g.member_count}件：{g.title_ja}
                  </p>
                  <Button size="sm" variant="outline" className="h-9 text-[12px]"
                    onClick={() => splitAll(g.id)}
                    data-testid={`approve-group-split-${g.id}`}>グループを分ける</Button>
                </div>
                <div className="space-y-1.5 pt-2">
                  {rows.filter((r) => r.group_id === g.id).map((r) => (
                    <ProposalRow key={r.id} row={r} onOpen={setOpenId} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-4 pt-3">
          {(data?.by_category || []).map((c) => {
            const list = c.rows.filter((r) => !r.group_id);
            if (list.length === 0) return null;
            return (
              <div key={c.key} data-testid={`approve-category-group-${c.key}`}>
                <h3 className="pb-1.5 text-[12.5px] font-semibold text-zinc-800">
                  {c.label_ja}
                  <span className="pl-2 text-[11px] font-normal text-zinc-500">{list.length}件</span>
                </h3>
                <div className="space-y-1.5">
                  {list.map((r) => <ProposalRow key={r.id} row={r} onOpen={setOpenId} />)}
                </div>
              </div>
            );
          })}
        </div>
      </Panel>

      {openId && (
        <ProposalDetail id={openId} onClose={() => { setOpenId(null); load(); }}
          onChanged={() => { load(); onChanged?.(); }} />
      )}

      <Panel testId="approve-note">
        <p className="flex items-center gap-2 text-[11.5px] text-zinc-500">
          <Prohibit size={13} />
          1件の確認だけでは全体に反映しません。承認した内容は記録として固定され、
          「効果を確認」で検証して有効にしたときだけ、今後の読み取りに使われます。
          モデルの重みは変更しません。
        </p>
      </Panel>
    </div>
  );
}
